/**
 * Value noise for the orb's interior fluid.
 *
 * Deliberately table-driven rather than trig-driven. The obvious
 * `Math.sin(x * 127.1 + y * 311.7) * 43758.5453` hash costs one `Math.sin` per
 * lattice corner, which works out at ~40 per pixel — several hundred thousand
 * per frame for a single orb, and multiplied again for every orb on screen.
 * An integer hash into a precomputed table gives the same character with no
 * transcendental maths in the inner loop.
 */
const TABLE_SIZE = 256;
const MASK = TABLE_SIZE - 1;
const table = new Float32Array(TABLE_SIZE);

// Seeded LCG, so the texture is byte-identical between reloads and machines.
let seed = 1337;
for (let i = 0; i < TABLE_SIZE; i += 1) {
  seed = (seed * 1664525 + 1013904223) >>> 0;
  table[i] = seed / 4294967296;
}

function hash(ix: number, iy: number): number {
  // XOR of two large odd multiples decorrelates the axes well enough for
  // smooth noise; `| 0` keeps it in int32 before masking.
  return table[(((ix * 73856093) ^ (iy * 19349663)) | 0) & MASK];
}

function valueNoise(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  // Smoothstep the interpolants so the lattice does not show as diamonds.
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);

  const n00 = hash(x0, y0);
  const n10 = hash(x0 + 1, y0);
  const n01 = hash(x0, y0 + 1);
  const n11 = hash(x0 + 1, y0 + 1);

  const a = n00 + (n10 - n00) * ux;
  const b = n01 + (n11 - n01) * ux;
  return a + (b - a) * uy;
}

/** Two octaves — enough structure for a soft fluid, cheap enough to run hot. */
export function fbm(x: number, y: number): number {
  return valueNoise(x, y) * 0.65 + valueNoise(x * 2.1, y * 2.1) * 0.35;
}
